import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import {
  checkStatusCodeSuccess,
  closeLabel,
  errorHandler,
} from "Components/constants/Common";
import {
  validationMessages,
  InputPlaceHolder,
  SelectPlaceHolder,
  emailRegex,
  numberRegex,
  zipcodeRegex,
} from "Components/constants/validation";
import BaseModal from "Components/Base/BaseModal";
import BaseInput from "Components/Base/BaseInput";
import { submitLabel } from "Components/constants/Role";
import { BaseSelect } from "Components/Base/BaseSelect";
import {
  AlternatePhoneNumberLabel,
  BillingAddressLabel,
  CityLabel,
  CountryLabel,
  CustomerLabel,
  CustomerNameLabel,
  CustomerType,
  CustomerTypeLabel,
  EmailLabel,
  PhoneNumberLabel,
  PostalCodeLabel,
  SameAsBillingAddressLabel,
  SameAsBillingAddressTooltipLabel,
  ShippingAddressLabel,
  StateLabel,
  StreetLabel,
} from "Components/constants/Staff";
import {
  listOfCity,
  listOfCountry,
  listOfState,
} from "Api/common";
import { gstinLabel, organizationLabel } from "Components/constants/Vendor";
import { createStaff } from "Api/staff";
import { QuotationLabel } from "Components/constants/Quotation";

interface ProductModalProps {
  open: boolean;
  toggle: () => void;
  onSuccess: (selectedProduct?: any) => void;
}

const basicDetailsSchema = yup.object({
  customerType: yup
    .string()
    .required(validationMessages.required(CustomerTypeLabel)),
  customerName: yup
    .string()
    .required(validationMessages.required(CustomerNameLabel)),
  phoneNumber: yup
    .string()
    .required(validationMessages.required(PhoneNumberLabel))
    .matches(
      numberRegex,
      validationMessages.contactLength(PhoneNumberLabel, 10)
    ),
  alternatePhone: yup
    .string()
    .matches(
      numberRegex,
      validationMessages.contactLength(AlternatePhoneNumberLabel, 10)
    ),
  email: yup
    .string()
    .required(validationMessages.required(EmailLabel))
    .matches(emailRegex, validationMessages.format(EmailLabel)),
});

const additionalDetailsSchema = yup.object({
  gst: yup
    .string()
    .nullable()
    .max(15, validationMessages.maxLength(gstinLabel, 15)),
  billingPostalCode: yup
    .string()
    .matches(
      zipcodeRegex,
      validationMessages.contactLength(PostalCodeLabel, 6)
    ),
  shippingPostalCode: yup
    .string()
    .matches(
      zipcodeRegex,
      validationMessages.contactLength(PostalCodeLabel, 6)
    ),
});

const CustomerModal: React.FC<ProductModalProps> = ({
  open,
  toggle,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [modalLoader, setModalLoader] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [city, setCity] = useState([]);

  useEffect(() => {
    if (open) {      
      fetchCountry();
    }
  }, [open]);

  const getValidationSchema = () => {
    switch (activeStep) {
      case 0:
        return basicDetailsSchema;
      case 1:
        return additionalDetailsSchema;
      default:
        return yup.object();
    }
  };

  const fetchCountry = () => {
    let data;
    const payload = {};
    listOfCountry(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setCountries(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.country_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      });
  };
  const fetchState = (id: number) => {
    let data;
    const payload = {
      condition: {
        country_id: id,
      },
    };
    listOfState(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setStates(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.state_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err: any) => {
        setStates([]);
        fetchCity(null);
        return err;
      });
  };
  const fetchCity = (id: any) => {
    let data;
    const payload = {
      condition: {
        state_id: id,
      },
    };
    listOfCity(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setCity(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.city_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        setCity([]);
        return err;
      });
  };

  const customerForm = useFormik({
    initialValues: {
      customerType: "",
      customerName: "",
      phoneNumber: "",
      alternatePhone: "",
      email: "",
      organization: "",
      gst: "",
      billingStreet: "",
      billingCountry: "",
      billingState: "",
      billingCity: "",
      billingPostalCode: "",
      shippingStreet: "",
      shippingCountry: "",
      shippingState: "",
      shippingCity: "",
      shippingPostalCode: "",
    },
    validationSchema: getValidationSchema(),
    onSubmit: async (values) => {
      setModalLoader(true);
      const payload = {
        type_of_user: CustomerLabel,
        name: values.customerName,
        contact_no: String(values.phoneNumber),
        alternative_contact_no: String(values.alternatePhone),
        email: values.email,
        customer_type: values.customerType,
        staffBankDetail: {
          organization: values.organization,
          gstin_no: values.gst,
        },
        staffAddress: {
          billing_address_1: values.billingStreet,
          billing_country_id: values.billingCountry || null,
          billing_state_id: values.billingState || null,
          billing_city_id: values.billingCity || null,
          billing_postal_code: values.billingPostalCode || null,
          shipping_same_as_billing: sameAsBilling,
          shipping_address_1: values.shippingStreet,
          shipping_country_id: values.shippingCountry || null,
          shipping_state_id: values.shippingState || null,
          shipping_city_id: values.shippingCity || null,
          shipping_postal_code: values.shippingPostalCode || null,
        },
      };
      createStaff(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            toggle();
            onSuccess({
              id: res?.data?.id,
            });
            customerForm.resetForm();
            setActiveStep(0);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          errorHandler(error);
        })
        .finally(() => setModalLoader(false));
    },
  });

  const handleSameAsBilling = () => {
    setSameAsBilling(!sameAsBilling);
    if (!sameAsBilling) {
      customerForm.setValues((prevValues) => ({
        ...prevValues,
        shippingStreet: prevValues.billingStreet,
        shippingCountry: prevValues.billingCountry,
        shippingState: prevValues.billingState,
        shippingCity: prevValues.billingCity,
        shippingPostalCode: prevValues.billingPostalCode,
      }));
    } else {
      customerForm.setValues((prevValues) => ({
        ...prevValues,
        shippingStreet: "",
        shippingCountry: "",
        shippingState: "",
        shippingCity: "",
        shippingPostalCode: "",
      }));
    }
  };

  const handleTabClick = async (step: number) => {
    if (step > activeStep) {
      const errors = await customerForm.validateForm();
      if (Object.keys(errors).length === 0) {
        setActiveStep(step);
      } else {
        const touchedFields = Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {} as Record<string, boolean>);
        customerForm.setTouched(touchedFields);
      }
    } else {
      setActiveStep(step);
    }
  };

  return (
    <BaseModal
      open={open}
      toggle={() => {
        toggle();
        customerForm.resetForm();
        setActiveStep(0);
      }}
      submit={() => customerForm.handleSubmit()}
      title={QuotationLabel.AddCustomer}
      closeLabel={closeLabel}
      submitLabel={submitLabel}
      closeLabelColor="danger"
      submitLabelColor="success"
      loader={modalLoader}
      size="lg"
    >
      <div className="form-group">
        <div className="card-header-tabs">
          <div className="card-body bg-white">
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeStep === 0 ? "active" : ""}`}
                  onClick={() => handleTabClick(0)}
                  type="button"
                >
                  {QuotationLabel.BasicDetails}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeStep === 1 ? "active" : ""}`}
                  onClick={() => handleTabClick(1)}
                  type="button"
                >
                  {QuotationLabel.AdditionalDetails}
                </button>
              </li>
            </ul>
            <div className="tab-content text-muted">
              {activeStep === 0 && (
                <div className="row g-3">
                  <div className="col-12">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <BaseSelect
                          name="customerType"
                          label={CustomerTypeLabel}
                          options={CustomerType}
                          value={customerForm.values.customerType}
                          placeholder={SelectPlaceHolder(CustomerTypeLabel)}
                          handleChange={(field: any, value: any) => {
                            customerForm.setFieldValue(field, value);
                          }}
                          handleBlur={customerForm.handleBlur}
                          error={customerForm.errors.customerType}
                          touched={customerForm.touched.customerType}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <BaseInput
                          name="customerName"
                          label={CustomerNameLabel}
                          placeholder={InputPlaceHolder(CustomerNameLabel)}
                          value={customerForm.values.customerName}
                          onChange={customerForm.handleChange}
                          handleBlur={customerForm.handleBlur}
                          error={customerForm.errors.customerName}
                          touched={customerForm.touched.customerName}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <BaseInput
                          name="phoneNumber"
                          type="number"
                          label={PhoneNumberLabel}
                          placeholder={InputPlaceHolder(PhoneNumberLabel)}
                          value={customerForm.values.phoneNumber}
                          onChange={customerForm.handleChange}
                          handleBlur={customerForm.handleBlur}
                          error={customerForm.errors.phoneNumber}
                          maxLength={10}
                          touched={customerForm.touched.phoneNumber}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <BaseInput
                          name="email"
                          label={EmailLabel}
                          placeholder={InputPlaceHolder(EmailLabel)}
                          type="email"
                          value={customerForm.values.email}
                          onChange={customerForm.handleChange}
                          handleBlur={customerForm.handleBlur}
                          error={customerForm.errors.email}
                          touched={customerForm.touched.email}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <BaseInput
                          name="alternatePhone"
                          type="number"
                          label={AlternatePhoneNumberLabel}
                          placeholder={InputPlaceHolder(
                            AlternatePhoneNumberLabel
                          )}
                          value={customerForm.values.alternatePhone}
                          maxLength={10}
                          onChange={customerForm.handleChange}
                          handleBlur={customerForm.handleBlur}
                          error={customerForm.errors.alternatePhone}
                          touched={customerForm.touched.alternatePhone}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activeStep === 1 && (
                <div>
                  <div className="row mt-4">
                    <div className="col-12 fs-5">{BillingAddressLabel}</div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseInput
                        name="organization"
                        label={organizationLabel}
                        placeholder={InputPlaceHolder(organizationLabel)}
                        type="text"
                        value={customerForm.values.organization}
                        onChange={customerForm.handleChange}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.organization}
                        touched={customerForm.touched.organization}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3 mt-3">
                      <BaseInput
                        name="gst"
                        label={gstinLabel}
                        placeholder={InputPlaceHolder(gstinLabel)}
                        type="text"
                        value={customerForm.values.gst}
                        onChange={(e) => {
                          if (e.target.value.length < 16) {
                            customerForm.handleChange(e);
                          }
                        }}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.gst}
                        touched={customerForm.touched.gst}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseInput
                        name="billingStreet"
                        type="textarea"
                        label={StreetLabel}
                        placeholder={InputPlaceHolder(StreetLabel)}
                        value={customerForm.values.billingStreet}
                        onChange={customerForm.handleChange}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.billingStreet}
                        touched={customerForm.touched.billingStreet}
                        rows={1}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseSelect
                        name="billingCountry"
                        label={CountryLabel}
                        options={countries}
                        value={customerForm.values.billingCountry}
                        placeholder={SelectPlaceHolder(CountryLabel)}
                        handleChange={(field: any, value: any) => {
                          customerForm.setFieldValue(field, value);
                          fetchState(value);
                        }}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.billingCountry}
                        touched={customerForm.touched.billingCountry}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseSelect
                        name="billingState"
                        label={StateLabel}
                        options={states}
                        value={customerForm.values.billingState}
                        placeholder={SelectPlaceHolder(StateLabel)}
                        handleChange={(field: any, value: any) => {
                          customerForm.setFieldValue(field, value);
                          fetchCity(value);
                        }}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.billingState}
                        touched={customerForm.touched.billingState}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseSelect
                        name="billingCity"
                        label={CityLabel}
                        options={city}
                        value={customerForm.values.billingCity}
                        placeholder={SelectPlaceHolder(CityLabel)}
                        handleChange={(field: any, value: any) => {
                          customerForm.setFieldValue(field, value);
                        }}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.billingCity}
                        touched={customerForm.touched.billingCity}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseInput
                        name="billingPostalCode"
                        type="number"
                        label={PostalCodeLabel}
                        placeholder={InputPlaceHolder(PostalCodeLabel)}
                        value={customerForm.values.billingPostalCode}
                        onChange={customerForm.handleChange}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.billingPostalCode}
                        touched={customerForm.touched.billingPostalCode}
                        maxLength={6}
                      />
                    </div>
                  </div>
                  <div className="row mt-4">
                    <div className="col-12 fs-5 d-flex justify-content-start align-items-center">
                      {ShippingAddressLabel}
                      <div className="d-flex flex-column align-items-center justify-content-start">
                        <div
                          className="form-check form-check-success form-switch form-switch-md ms-3"
                          dir="ltr"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input shadow-none"
                            id="customSwitchsizemd"
                            checked={sameAsBilling}
                            onChange={handleSameAsBilling}
                          />
                          <label
                            className="form-check-label custom-label fs-5"
                            htmlFor="customSwitchsizemd"
                          >
                            {SameAsBillingAddressLabel}
                          </label>
                          <i className="mdi mdi-help-circle-outline ms-1 mt-1 text-info fs-5 cursor-pointer align-middle tooltip-container-data">
                            <span className="tooltip-text font-weight-normal">
                              {SameAsBillingAddressTooltipLabel}
                            </span>
                          </i>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseInput
                        name="shippingStreet"
                        type="textarea"
                        label={StreetLabel}
                        placeholder={InputPlaceHolder(StreetLabel)}
                        value={customerForm.values.shippingStreet}
                        onChange={customerForm.handleChange}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.shippingStreet}
                        touched={customerForm.touched.shippingStreet}
                        disabled={sameAsBilling}
                        rows={1}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseSelect
                        name="shippingCountry"
                        label={CountryLabel}
                        options={countries}
                        value={customerForm.values.shippingCountry}
                        placeholder={SelectPlaceHolder(CountryLabel)}
                        handleChange={(field: any, value: any) => {
                          customerForm.setFieldValue(field, value);
                          fetchState(value);
                        }}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.shippingCountry}
                        touched={customerForm.touched.shippingCountry}
                        isDisabled={sameAsBilling}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseSelect
                        name="shippingState"
                        label={StateLabel}
                        options={states}
                        value={customerForm.values.shippingState}
                        placeholder={SelectPlaceHolder(StateLabel)}
                        handleChange={(field: any, value: any) => {
                          customerForm.setFieldValue(field, value);
                          fetchCity(value);
                        }}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.shippingState}
                        touched={customerForm.touched.shippingState}
                        isDisabled={sameAsBilling}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseSelect
                        name="shippingCity"
                        label={CityLabel}
                        options={city}
                        value={customerForm.values.shippingCity}
                        placeholder={SelectPlaceHolder(CityLabel)}
                        handleChange={(field: any, value: any) => {
                          customerForm.setFieldValue(field, value);
                        }}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.shippingCity}
                        touched={customerForm.touched.shippingCity}
                        isDisabled={sameAsBilling}
                      />
                    </div>
                    <div className="col-md-3 col-sm-6 col-12 mt-3">
                      <BaseInput
                        name="shippingPostalCode"
                        type="number"
                        label={PostalCodeLabel}
                        placeholder={InputPlaceHolder(PostalCodeLabel)}
                        value={customerForm.values.shippingPostalCode}
                        onChange={customerForm.handleChange}
                        handleBlur={customerForm.handleBlur}
                        error={customerForm.errors.shippingPostalCode}
                        touched={customerForm.touched.shippingPostalCode}
                        disabled={sameAsBilling}
                        maxLength={6}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default CustomerModal;
